import ProctorVerdict from "@/components/wishire/ProctorVerdict";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { companyTests, companyTestStart, companyTestSubmit } from "@/lib/api";
import { PaywallCard } from "@/pages/app/MockTestPage";
import { practiceAllowance } from "@/lib/api";
import {
  Building2, Loader2, Camera, CameraOff, ShieldCheck, ShieldAlert,
  Clock, CheckCircle2, XCircle, RotateCcw, Lock, Trophy, ArrowRight,
} from "lucide-react";

/* Company-pattern aptitude mocks: the real 2026 section structure of
   TCS NQT / Infosys / Wipro / Accenture / Cognizant, sprint-scaled, with
   fresh questions each attempt, sectional cutoffs and company bands. */

const COMPANY_TONE = {
  TCS: "bg-blue-50 text-blue-700 border-blue-200",
  Infosys: "bg-indigo-50 text-indigo-700 border-indigo-200",
  Wipro: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Accenture: "bg-purple-50 text-purple-700 border-purple-200",
  Cognizant: "bg-sky-50 text-sky-700 border-sky-200",
  Capgemini: "bg-cyan-50 text-cyan-700 border-cyan-200",
  Deloitte: "bg-green-50 text-green-800 border-green-200",
  Zoho: "bg-red-50 text-red-700 border-red-200",
  HCLTech: "bg-violet-50 text-violet-700 border-violet-200",
};

export default function CompanyTestPage() {
  const [params] = useSearchParams();
  const driveId = params.get("drive");
  const driveCompany = params.get("company");
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stage, setStage] = useState("intro"); // intro | starting | live | grading | result
  const [test, setTest] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);
  const [allowance, setAllowance] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [startingId, setStartingId] = useState(null);

  // Proctoring (same contract as the role mock test)
  const [cameraActive, setCameraActive] = useState(false);
  const [tabSwitches, setTabSwitches] = useState(0);
  const webcamRef = useRef(null);
  const streamRef = useRef(null);
  const tabRef = useRef(0);
  const stageRef = useRef("intro");
  useEffect(() => { stageRef.current = stage; }, [stage]);

  const loadMeta = () => {
    companyTests().then(({ data }) => setMeta(data)).catch(() => setMeta(null)).finally(() => setLoading(false));
    practiceAllowance().then(({ data }) => setAllowance(data)).catch(() => {});
  };
  useEffect(() => { loadMeta(); return () => stopCamera(); }, []); // eslint-disable-line

  useEffect(() => {
    const onVis = () => {
      if (document.hidden && stageRef.current === "live") {
        tabRef.current += 1;
        setTabSwitches(tabRef.current);
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" }, audio: false });
      streamRef.current = stream;
      if (webcamRef.current) webcamRef.current.srcObject = stream;
      setCameraActive(true);
    } catch (_) { setCameraActive(false); }
  };
  const stopCamera = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setCameraActive(false);
  };

  const begin = async (startArg) => {
    setErr(null); setStartingId(typeof startArg === "string" ? startArg : "drive");
    try {
      await startCamera();
      const { data } = await companyTestStart(startArg);
      setTest(data); setAnswers({}); tabRef.current = 0; setTabSwitches(0);
      setSecondsLeft(data.duration_min * 60);
      setStage("live");
    } catch (e) {
      stopCamera();
      if (e?.response?.status === 402) { loadMeta(); setErr("paywall"); }
      else setErr(e?.response?.data?.detail || "Could not start the test — try again.");
    } finally { setStartingId(null); }
  };

  const finish = useCallback(async () => {
    setStage("grading");
    stopCamera();
    try {
      const { data } = await companyTestSubmit(test.test_id, {
        answers, tab_switches: tabRef.current, camera_active: streamRef.current !== null || cameraActive,
      });
      setResult(data.result); setStage("result"); loadMeta();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Grading failed — your answers are saved; try submitting again.");
      setStage("live");
    }
  }, [test, answers, cameraActive]); // eslint-disable-line

  useEffect(() => {
    if (stage !== "live") return;
    const t = setInterval(() => setSecondsLeft((s) => {
      if (s <= 1) { clearInterval(t); finish(); return 0; }
      return s - 1;
    }), 1000);
    return () => clearInterval(t);
  }, [stage, finish]);

  const mm = String(Math.floor(secondsLeft / 60)).padStart(2, "0");
  const ss = String(secondsLeft % 60).padStart(2, "0");

  if (loading) return <div className="container-x py-16 text-stone-500 flex items-center gap-2"><Loader2 size={18} className="animate-spin" /> Loading…</div>;

  if (meta && !meta.eligible && !driveId) return (
    <div className="container-x py-16 max-w-xl" data-testid="ct-ineligible">
      <div className="rounded-3xl border border-stone-200 bg-white p-10 text-center">
        <Lock size={32} className="mx-auto text-stone-300" />
        <h1 className="font-display text-2xl mt-4">Company-pattern tests</h1>
        <p className="text-sm text-stone-600 mt-3 leading-relaxed">{meta.reason}</p>
      </div>
    </div>
  );

  return (
    <div className="container-x py-10 md:py-14" data-testid="ct-page">
      <div className="mb-8">
        <span className="overline">Your campus drives · mock tests</span>
        <h1 className="font-display text-4xl md:text-5xl mt-2 leading-tight">Practice the companies <span className="italic text-stone-500">coming to your campus.</span></h1>
        <p className="mt-3 text-stone-600 max-w-2xl">Every drive you're eligible for comes with a proctored mock in that company's real test pattern — same section mix, timing pressure and sectional cutoffs, fresh questions every attempt. Take it before you apply.</p>
      </div>

      {stage === "intro" && (
        err === "paywall" || meta?.allowance?.remaining === 0 ? (
          <PaywallCard allowance={allowance} onPurchased={() => { setErr(null); loadMeta(); }} />
        ) : (
          <>
            {err && err !== "paywall" && <p className="mb-5 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-2">{err}</p>}
            {driveId && (
              <div className="mb-6 rounded-3xl border-2 border-[#C2410C]/30 bg-amber-50/60 p-6 flex flex-wrap items-center justify-between gap-4" data-testid="ct-drive-banner">
                <div>
                  <p className="text-xs font-mono uppercase tracking-widest text-[#C2410C]">Your campus drive</p>
                  <h2 className="font-display text-2xl mt-1">Mock test for the {driveCompany || "company"} drive</h2>
                  <p className="text-sm text-stone-600 mt-1 max-w-lg">Same section mix and cutoffs {driveCompany ? `${driveCompany} uses` : "the company uses"} — check your readiness before you apply. Counts as one proctored test.</p>
                </div>
                <button onClick={() => begin({ drive_id: driveId })} disabled={startingId !== null} data-testid="ct-start-drive"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#C2410C] text-white text-sm font-semibold hover:bg-[#9a3412] disabled:opacity-60">
                  {startingId === "drive" ? <Loader2 size={15} className="animate-spin" /> : <ShieldCheck size={15} />} Start {driveCompany || "drive"} mock
                </button>
              </div>
            )}
            {(meta?.tests || []).length === 0 && !driveId && (
              <div className="rounded-3xl border border-stone-200 bg-white p-10 text-center" data-testid="ct-empty">
                <Lock size={30} className="mx-auto text-stone-300" />
                <h2 className="font-display text-2xl mt-4">No drive mocks yet</h2>
                <p className="text-sm text-stone-600 mt-2 max-w-md mx-auto leading-relaxed">
                  {meta?.note || "When your placement cell opens a drive you're eligible for, a proctored mock in that company's real pattern appears here."}
                </p>
              </div>
            )}
            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
              {(meta?.tests || []).map((p) => (
                <div key={p.drive_id} className="rounded-3xl border border-stone-200 bg-white p-6 flex flex-col" data-testid={`ct-card-${p.drive_id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${COMPANY_TONE[p.company] || "bg-stone-50 text-stone-700 border-stone-200"}`}>
                      <Building2 size={12} /> {p.company}
                    </span>
                    {p.best && (
                      <span className="inline-flex items-center gap-1 text-xs font-mono text-amber-600"><Trophy size={12} /> best {p.best.best_score}</span>
                    )}
                  </div>
                  <h2 className="font-display text-2xl mt-3">{p.name}</h2>
                  <p className="text-xs text-stone-500 mt-1">
                    Drive: {p.drive_title || p.company}{p.role ? ` · ${p.role}` : ""}
                    {p.already_applied ? " · applied ✓" : ""}
                    {p.deadline ? ` · apply by ${new Date(p.deadline).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}` : ""}
                  </p>
                  <div className="mt-3 space-y-1.5">
                    {p.sections.map((s) => (
                      <div key={s.key} className="flex items-center justify-between text-sm">
                        <span className="text-stone-700">{s.label}</span>
                        <span className="font-mono text-xs text-stone-400">{s.questions}Q · {s.minutes}m</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-[11px] text-stone-400 mt-3 leading-relaxed flex-1">{p.real_pattern}</p>
                  <div className="mt-4 flex items-center justify-between">
                    <span className="text-xs text-stone-500 inline-flex items-center gap-1"><Clock size={12} /> {p.total_questions}Q · {p.total_minutes} min · sectional cutoffs</span>
                  </div>
                  <button onClick={() => begin({ drive_id: p.drive_id })} disabled={startingId !== null} data-testid={`ct-start-${p.drive_id}`}
                    className="mt-3 w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-stone-900 text-white text-sm font-medium hover:bg-stone-700 disabled:opacity-60">
                    {startingId !== null ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />} Start proctored mock
                  </button>
                </div>
              ))}
            </div>
            {(meta?.locked || []).length > 0 && (
              <div className="mt-8" data-testid="ct-locked">
                <p className="text-xs font-mono uppercase tracking-widest text-stone-400 mb-3">Also visiting your campus — you're not eligible yet</p>
                <div className="space-y-2">
                  {meta.locked.map((l, i) => (
                    <div key={i} className="flex flex-wrap items-baseline gap-x-3 gap-y-1 rounded-2xl border border-stone-200 bg-stone-50 px-4 py-3">
                      <span className="text-sm font-medium text-stone-700 inline-flex items-center gap-1.5"><Lock size={12} /> {l.company}</span>
                      <span className="text-xs text-stone-500">{(l.reasons || []).join(" ")}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {meta?.allowance && (
              <p className="mt-5 text-xs text-stone-500" data-testid="ct-remaining">
                {meta.allowance.remaining} proctored test{meta.allowance.remaining !== 1 ? "s" : ""} left — company mocks and role mocks share the same allowance.
              </p>
            )}
          </>
        )
      )}

      {stage === "live" && test && (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-6 items-start">
          <div className="space-y-6">
            {test.sections.map((s) => (
              <div key={s.key}>
                <div className="flex items-center justify-between mb-3">
                  <h2 className="font-display text-xl">{s.label}</h2>
                  <span className="text-xs font-mono text-stone-400">{s.mcq.length}Q · suggested {s.minutes}m</span>
                </div>
                <div className="space-y-4">
                  {s.mcq.map((q) => (
                    <div key={q.index} className="rounded-2xl border border-stone-200 bg-white p-5" data-testid={`ct-q-${q.index}`}>
                      <p className="text-sm font-medium text-stone-900 whitespace-pre-wrap"><span className="text-stone-400 font-mono mr-2">Q{q.index + 1}</span>{q.q}</p>
                      <div className="mt-3 grid sm:grid-cols-2 gap-2">
                        {q.options.map((op, oi) => (
                          <label key={oi} className={`flex items-center gap-2.5 px-3 py-2 rounded-xl border text-sm cursor-pointer ${answers[String(q.index)] === oi ? "border-stone-900 bg-stone-50 font-medium" : "border-stone-200 hover:border-stone-400"}`}>
                            <input type="radio" name={`q${q.index}`} checked={answers[String(q.index)] === oi}
                              onChange={() => setAnswers((a) => ({ ...a, [String(q.index)]: oi }))} className="accent-[#C2410C]" />
                            {op}
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            <button onClick={finish} data-testid="ct-submit"
              className="w-full px-6 py-3.5 rounded-full bg-stone-900 text-white font-medium hover:bg-stone-700">
              Submit for grading
            </button>
          </div>

          <div className="lg:sticky lg:top-6 space-y-3">
            <div className={`rounded-2xl p-4 text-center font-mono text-3xl font-bold ${secondsLeft < 120 ? "bg-red-50 text-red-600 border border-red-200" : "bg-stone-900 text-amber-300"}`} data-testid="ct-timer">
              {mm}:{ss}
            </div>
            <div className="rounded-2xl border border-stone-200 bg-white p-3">
              <video ref={webcamRef} autoPlay muted playsInline className="w-full rounded-xl bg-stone-900 aspect-video object-cover" />
              <div className="mt-2 flex items-center justify-between text-xs">
                <span className={`inline-flex items-center gap-1 ${cameraActive ? "text-emerald-600" : "text-red-600"}`}>
                  {cameraActive ? <Camera size={12} /> : <CameraOff size={12} />} {cameraActive ? "Proctoring on" : "Camera off"}
                </span>
                <span className={`inline-flex items-center gap-1 ${tabSwitches === 0 ? "text-emerald-600" : "text-red-600"}`}>
                  {tabSwitches === 0 ? <ShieldCheck size={12} /> : <ShieldAlert size={12} />} {tabSwitches} switch{tabSwitches !== 1 ? "es" : ""}
                </span>
              </div>
            </div>
            <p className="text-[11px] text-stone-400 px-1">{test.name} · sections have a typical 60% cutoff each — pace yourself.</p>
          </div>
        </div>
      )}

      {stage === "grading" && (
        <div className="rounded-3xl border border-stone-200 bg-white p-12 text-center">
          <Loader2 size={28} className="animate-spin mx-auto text-[#C2410C]" />
          <p className="mt-4 font-display text-2xl">Grading against the {test?.company} pattern…</p>
        </div>
      )}

      {stage === "result" && result && (
        <div>
          <div className="rounded-3xl bg-stone-900 text-stone-50 p-7 relative overflow-hidden wsh-grain mb-6" data-testid="ct-result">
            <div className="absolute inset-0 opacity-40 pointer-events-none" style={{ background: "radial-gradient(420px 240px at 90% 0%, rgba(217,119,6,0.4), transparent 60%)" }} />
            <div className="relative">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <span className="overline" style={{ color: "rgba(253,251,247,0.55)" }}>{test?.name}</span>
                  <p className="font-display text-6xl mt-1">{result.score}<span className="text-2xl text-stone-400">/100</span></p>
                  <p className="text-amber-300 font-medium mt-1">{result.band}</p>
                </div>
                <div className="text-sm space-y-1.5 text-stone-300">
                  <p className={result.cleared_all_sections ? "text-emerald-400" : "text-red-400"}>
                    {result.cleared_all_sections ? "✓ All sectional cutoffs cleared" : "✗ Sectional cutoff missed"}
                  </p>
                  <ProctorVerdict p={result.proctor} integrity={result.integrity} dark />
                  {result.time_expired && <p className="text-amber-300">Time expired</p>}
                </div>
              </div>
              <p className="text-stone-300 text-sm mt-3 max-w-2xl">{result.verdict}</p>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            {result.sections.map((s) => (
              <div key={s.key} className={`rounded-2xl border p-4 ${s.clears_cutoff ? "border-emerald-200 bg-emerald-50/50" : "border-red-200 bg-red-50/50"}`} data-testid={`ct-sec-${s.key}`}>
                <p className="text-xs font-medium text-stone-600">{s.label}</p>
                <p className="font-display text-3xl mt-1">{s.percent}<span className="text-sm text-stone-400">%</span></p>
                <p className="text-[11px] text-stone-500 mt-0.5">{s.correct}/{s.total} correct · cutoff {result.section_cutoff}% {s.clears_cutoff ? "✓" : "✗"}</p>
              </div>
            ))}
          </div>

          <div className="space-y-3">
            {result.questions.map((r) => (
              <div key={r.index} className={`rounded-2xl border px-5 py-3.5 ${r.correct ? "border-emerald-200 bg-emerald-50/50" : "border-red-200 bg-red-50/50"}`}>
                <p className="text-sm font-medium flex items-start gap-2">
                  {r.correct ? <CheckCircle2 size={15} className="text-emerald-600 mt-0.5 shrink-0" /> : <XCircle size={15} className="text-red-500 mt-0.5 shrink-0" />}
                  <span className="whitespace-pre-wrap"><span className="text-[10px] font-mono uppercase tracking-widest text-stone-400 mr-2">{r.section}</span>{r.q}</span>
                </p>
                {!r.correct && <p className="text-xs text-stone-600 mt-1 ml-6">You: {r.your_answer || "—"} · Correct: <b>{r.correct_answer}</b></p>}
              </div>
            ))}
          </div>
          <button onClick={() => { setStage("intro"); setResult(null); setTest(null); loadMeta(); }}
            className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-stone-300 text-sm font-medium hover:border-stone-900">
            <RotateCcw size={15} /> Try another company <ArrowRight size={13} />
          </button>
        </div>
      )}
    </div>
  );
}
